import {
  convertCommonFileSizeToFriendlyFormat,
  getFileMultiplicationFactorByUnitName,
} from "helpers/data-helpers/assetFinanceFileUploadHelpers";
import { PhoneNumberUtil } from "google-libphonenumber";

export function checkFileType(allowedTypes: any[][], file: { type: any }) {
  return allowedTypes.includes([file.type]);
}

/**
 * @function
 *
 * @param maximumSize {number}
 * @param {string} unitOfMeasure One of {@link ALLOWED_FILE_SIZE_NAMES}
 * @param {object} file File to check
 * @returns {boolean}
 * @example
 *  functionUsingEventInput(event) => {
 *    const file = event.target.files[0];
 *    const fileSize = checkFileSize(5, ALLOWED_FILE_SIZE_NAMES.MEGA_BYTE, file);
 *  }
 */
export function checkMaxFileSize(
  maximumSize: number,
  unitOfMeasure: string,
  file: { name?: string; type?: string; size: any }
) {
  const fileSize = file.size;
  const maxSize = getSizeInBytesFromUnitName(maximumSize, unitOfMeasure);
  let fileSizeIsOK = maxSize >= fileSize;

  return fileSizeIsOK;
}

/**
 * @function
 *
 * @param {number} fileSizeInUnits Size you want returned in the unit of measure supplied.
 * @param {string} unitOfMeasure One of {@link ALLOWED_FILE_SIZE_NAMES}
 * @returns {number} The equivalent size in bytes.
 * @example
 *  const fileLimit = getFileSizeFromTidyName(5, ALLOWED_FILE_SIZE_NAMES.MEGA_BYTE);
 *
 */
export function getSizeInBytesFromUnitName(
  fileSizeInUnits: number,
  unitOfMeasure: string
) {
  return fileSizeInUnits * getFileMultiplicationFactorByUnitName(unitOfMeasure);
}

/**
 * @param {number} uploadLimitRelativeSize The number of units to allow of the given {@link unitOfMeasure}.
 * @param {string} unitOfMeasure one of {@link ALLOWED_FILE_SIZE_NAMES}
 * @param {number} attemptedFileSize Value attained by passing `file.size` which is in bytes.
 * @returns {string} A predictable formatted string for uniform file size errors.
 */
export function generateFileSizeError(
  uploadLimitRelativeSize: number,
  unitOfMeasure: string,
  attemptedFileSize: number
) {
  const maxSize = getSizeInBytesFromUnitName(
    uploadLimitRelativeSize,
    unitOfMeasure
  );
  const userFriendlyFileSizeLimitText = convertCommonFileSizeToFriendlyFormat(
    maxSize,
    false
  );
  const userFriendlyAttemptedUploadSize = convertCommonFileSizeToFriendlyFormat(
    attemptedFileSize,
    false
  );

  const lineBreak = "\n";

  return `
    File size is over ${userFriendlyFileSizeLimitText} limit: 
    ${userFriendlyAttemptedUploadSize}
    ${lineBreak}
    Please try again with a file of the correct size.`;
}

export function validatePhone(value: string, country: string): boolean {
  if (!value || !country) return false;

  const phoneUtil = PhoneNumberUtil.getInstance();
  try {
    const number = phoneUtil.parseAndKeepRawInput(value, country);
    return phoneUtil.isValidNumber(number);
  } catch (e) {
    return false;
  }
}
